import {flip, offset, shift, useFloating} from '@floating-ui/react';
import {Show, Text} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {FocusEvent, ReactNode, useRef, useState} from 'react';

import {isNil} from 'ramda';

import {
  containsFocusableElements,
  suffixTestId,
  TestIdProps,
  useBoolean,
  useOnMount,
} from 'shared/web';

import {useComponentsTheme} from '../../hooks/useComponentsTheme';
import {Placement} from '../../types/Placement';
import {Portal} from '../Portal/components/Portal';
import {PortalManager} from '../Portal/components/PortalManager';

type TooltipPlacement = Exclude<Placement, 'auto'>;

const OFFSET = 10;

export interface TooltipProps extends TestIdProps {
  children: ReactNode;
  label?: ReactNode | string;
  description?: ReactNode;
  placement?: TooltipPlacement;
  overflowWrap?: 'anywhere';
  childrenWrapperDisplay?: 'flex' | 'block';
  isOpen?: boolean;
  isDisabled?: boolean;
  openDelay?: number;
  closeDelay?: number;
  /**
   * Keeps the tooltip open while the pointer is over its content,
   * so interactive elements (links) inside the tooltip can be clicked.
   * Applies a default closeDelay of 150 ms unless closeDelay is set.
   */
  isInteractive?: boolean;
  hasAutoWidth?: boolean;
  offset?: number;
  shouldWrapperStretch?: boolean;
  onOpen?(): void;
  onClose?(): void;
}

const childrenWrapperVariants = cva('', {
  variants: {
    display: {
      block: 'block',
      flex: 'flex',
    },
    shouldStretch: {
      true: 'w-full',
      false: 'w-auto',
    },
  },
  defaultVariants: {
    display: 'block',
    shouldStretch: false,
  },
});

const contentWrapperVariants = cva(
  'rounded-[var(--radius-sm)] bg-[var(--color-palettes-neutral-900-100)] px-2 py-1',
  {
    variants: {
      hasAutoWidth: {
        true: 'max-w-[50vw]',
        false: 'max-w-[calc(var(--eag-space-step,0.25)*58rem)]',
      },
    },
    defaultVariants: {
      hasAutoWidth: false,
    },
  }
);

export function Tooltip(props: TooltipProps) {
  const theme = useComponentsTheme();
  const [isOpen, setOpen, setClose] = useBoolean(props.isOpen);
  const [hasFocusableChildren, setHasFocusableChildren] = useState(false);
  const [hasExplicitlyNonFocusableChildren, setHasExplicitlyNonFocusableChildren] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [setReference] = useState(() => (node: HTMLDivElement | null): void => {
    if (isNil(node)) {
      return;
    }

    wrapperRef.current = node;
    setReferenceElement(node);
  });

  const {refs, floatingStyles} = useFloating({
    placement: props.placement,
    middleware: [offset(props.offset ?? OFFSET), shift(), flip()],
    strategy: props.hasAutoWidth ? 'fixed' : 'absolute',
    elements: {
      reference: referenceElement,
    },
  });

  useOnMount(function detectFocusableTooltipChildren() {
    setHasFocusableChildren(containsFocusableElements(wrapperRef.current));
    setHasExplicitlyNonFocusableChildren(
      (wrapperRef.current?.querySelectorAll('[tabindex="-1"]').length ?? 0) > 0
    );
  });

  useOnMount(function registerTooltipTimerCleanup() {
    return function clearTooltipTimerOnUnmount() {
      if (isNil(timerRef.current)) {
        return;
      }

      clearTimeout(timerRef.current);
    };
  });

  const handleFocus = (event: FocusEvent<HTMLDivElement>) => {
    const isKeyboardNavigation = event.target?.matches(':focus-visible');

    if (isKeyboardNavigation) {
      handleOpen();
    }
  };

  const effectiveCloseDelay = props.closeDelay ?? (props.isInteractive ? 150 : undefined);

  const handleOpen = () => {
    const shouldOpen = props.isOpen ?? true;

    if (props.isDisabled || !shouldOpen) {
      return;
    }

    if (!isNil(timerRef.current)) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (isNil(props.openDelay)) {
      setOpen();
      props.onOpen?.();
      return;
    }

    timerRef.current = setTimeout(() => {
      setOpen();
      props.onOpen?.();
    }, props.openDelay);
  };

  const handleClose = () => {
    const shouldClose = props.isOpen !== true;

    if (!shouldClose) {
      return;
    }

    if (!isNil(timerRef.current)) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (isNil(effectiveCloseDelay)) {
      setClose();
      props.onClose?.();
      return;
    }

    timerRef.current = setTimeout(() => {
      setClose();
      props.onClose?.();
    }, effectiveCloseDelay);
  };

  if (isNil(props.label) && isNil(props.description)) {
    return <>{props.children}</>;
  }

  return (
    <>
      <div
        className={cn(
          childrenWrapperVariants({
            display: props.childrenWrapperDisplay,
            shouldStretch: props.shouldWrapperStretch,
          })
        )}
        ref={setReference}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onFocus={handleFocus}
        onBlur={handleClose}
        tabIndex={
          hasFocusableChildren || hasExplicitlyNonFocusableChildren || props.isDisabled
            ? undefined
            : 0
        }
      >
        {props.children}
      </div>
      <Show when={isOpen}>
        <PortalManager zIndex={theme.zIndices.TOOLTIP}>
          <Portal>
            <span
              ref={refs.setFloating}
              style={floatingStyles}
              {...(props.isInteractive
                ? {onMouseEnter: handleOpen, onMouseLeave: handleClose}
                : {})}
            >
              <div className={cn(contentWrapperVariants({hasAutoWidth: props.hasAutoWidth}))}>
                <Show when={props.label}>
                  <Text size="xSmall" alternative color="white" overflowWrap={props.overflowWrap}>
                    {props.label}
                  </Text>
                </Show>
                <Show when={props.description}>
                  <Text
                    size="xSmall"
                    color="white"
                    data-testid={suffixTestId(`tooltipDescription`, props)}
                    overflowWrap={props.overflowWrap}
                  >
                    {props.description}
                  </Text>
                </Show>
              </div>
            </span>
          </Portal>
        </PortalManager>
      </Show>
    </>
  );
}
